import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ExclamationIcon, TrashIcon } from "@heroicons/react/outline";
import ExportDropdown from "./ExportDropdown";
import { Button } from "ui";
import { EXPORT_DROPDOWN_NAME } from "./utils";

type BaseProps = {
  label: React.ReactNode;
  tableName: React.ReactNode;
  onConfirm: (info: { method: string; type: string }) => void;
  rowsCount: number;
  dataCount: number;
};

type ButtonWithConfirmModalProps = BaseProps & {
  type: string;
  noButton?: boolean;
  forceOpen?: boolean;
  onChangeOpenStatus?: (isOpen: boolean) => void;
  onDeselectAll: () => void;
  hasPermission: boolean;
};

type TableConfirmationProps = BaseProps & {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  actionForConfirmation: {
    method: string;
    type: string;
  };
};

export const TableConfirmation = ({
  open,
  label,
  tableName,
  setOpen,
  onConfirm,
  actionForConfirmation,
  rowsCount,
  dataCount,
}: TableConfirmationProps) => {
  const deleteButtonRef = useRef(null);
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-20 overflow-y-auto"
        initialFocus={deleteButtonRef}
        onClose={setOpen}
      >
        <div className="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-foundations-subtler/50 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:h-screen sm:align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block overflow-hidden rounded-lg bg-foundations-default text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
              <div className="bg-foundations-default px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-danger-subtler sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationIcon
                      className="h-6 w-6 text-danger"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-foundations-boldest"
                    >
                      {label} {tableName}s
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm font-normal text-foundations-subtle">
                        Are you sure you want to {label}{" "}
                        <span className="font-bold">
                          {actionForConfirmation.type === "export_all"
                            ? `all ${dataCount}`
                            : rowsCount}{" "}
                          {tableName}s
                        </span>
                        {label === "Delete" && (
                          <span>
                            ? All of your data will be permanently removed from
                            our servers forever. This action cannot be undone.
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col-reverse gap-3 px-4 pb-6 pt-2 sm:flex-row sm:px-6">
                <Button
                  className="flex-1"
                  variant="secondary"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="danger"
                  className="flex-1"
                  onClick={() => {
                    onConfirm(actionForConfirmation);
                    setOpen(false);
                  }}
                  ref={deleteButtonRef}
                >
                  {label}
                </Button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

const ButtonWithConfirmModal: React.FC<ButtonWithConfirmModalProps> = ({
  label,
  type,
  tableName,
  rowsCount,
  dataCount,
  onConfirm,
  noButton,
  forceOpen,
  hasPermission,
  onChangeOpenStatus,
  onDeselectAll,
}) => {
  const [open, setOpen] = useState(false);
  const [actionForConfirmation, setActionForConfirmation] = useState({
    method: "",
    type,
  });

  useEffect(() => {
    if (noButton) {
      setOpen(forceOpen!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpen]);

  useEffect(() => {
    if (noButton) {
      onChangeOpenStatus!(open);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const getExportDropdownOptions = useCallback((): [string, string][] => {
    return [
      ["export_selected", `Export ${rowsCount} ${tableName}s on this page`],
      ["export_all", `Export all ${dataCount} ${tableName}s`],
      ["deselect_all", "Deselect all"],
    ];
  }, [rowsCount, tableName, dataCount]);

  if (!hasPermission) {
    return null;
  }

  return (
    <React.Fragment>
      {!noButton ? (
        <Fragment>
          {type === EXPORT_DROPDOWN_NAME ? (
            <ExportDropdown
              options={getExportDropdownOptions()}
              onSelect={(selection) => {
                if (selection[0] === "deselect_all") {
                  onDeselectAll();
                } else {
                  setOpen(true);
                  setActionForConfirmation({
                    method: "post",
                    type: selection[0],
                  });
                }
              }}
            />
          ) : (
            <Button
              type="button"
              onClick={() => setOpen(true)}
              disabled={!hasPermission}
              variant="secondary"
              colorScheme={label === "Delete" ? "danger" : null}
            >
              {label} Selected
              {label === "Delete" && (
                <TrashIcon
                  className="-mr-0.5 ml-2 h-4 w-4 text-inherit"
                  aria-hidden="true"
                />
              )}
            </Button>
          )}
        </Fragment>
      ) : null}
      <TableConfirmation
        label={label}
        tableName={tableName}
        onConfirm={onConfirm}
        open={open}
        setOpen={setOpen}
        actionForConfirmation={actionForConfirmation}
        rowsCount={rowsCount}
        dataCount={dataCount}
      />
    </React.Fragment>
  );
};
export default ButtonWithConfirmModal;
